// src/views/Manufacturing/ManufacturingUpdate.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Spinner from 'components/Spinner';
import moment from 'moment';
import MaterialTable from 'material-table';
import {
  ThemeProvider,
  createTheme,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  IconButton,
  LinearProgress,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import CheckCircleSharpIcon from '@mui/icons-material/CheckCircleSharp';
import ShowToast from 'components/ShowToast';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { TYPE_SUCCESS, TYPE_FAIL, INTERVAL, SUPER_ADMIN } from 'assets/constants/Constants';
import { BOM_STAGES } from 'assets/constants/Constants';
import {
  getBomsByStage,
  updatestepfrombom,
  getS3PresignedUrl,
  downloadDocumentBucket,
  getPartList,
  getStdProcessList,
  updateBomItemPartial
} from 'lib/axiosUtils';
import { HTTP_HEADER_KEYS, HTTP_CONTENT_TYPES } from 'assets/constants/apiConstants';
import './manufacturing.css';
import { CALENDER_ICON, BOM_ICON, FlashOnIcon} from 'assets/constants/Icons';
import SaveIcon from '@mui/icons-material/Save';

const idOf = (x) =>
  (x?.$oid || x?._id || (typeof x === 'string' ? x : x?.toString?.()))?.trim();

const ManufacturingUpdate = (props) => {
  const initialUser =
    props?.auth?.user ||
    JSON.parse(localStorage.getItem('userData') || 'null') ||
    {};
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [partMap, setPartMap] = useState({});
  const [processMap, setProcessMap] = useState({});

  const theme = useMemo(
    () => createTheme({ typography: { fontSize: 16 } }),
    []
  );

  /* fetch data */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bomRes, partRes, procRes] = await Promise.all([
          getBomsByStage({}, { stage: BOM_STAGES.MANUFACTURING_EXECUTION }),
          getPartList({}, {}),
          getStdProcessList({}, {}),
        ]);

        const parts = partRes?.data?.result || [];
        const processes = procRes?.data?.result || [];
        setPartMap(Object.fromEntries(parts.map((p) => [p._id, p.part_name])));
        setProcessMap(
          Object.fromEntries(processes.map((p) => [p._id, p.process_title]))
        );

        const boms = bomRes?.data?.result || [];
        const allItems = boms
          .filter((bom) => bom.supplier_id === initialUser._id)
          .flatMap((bom) =>
          (bom.items || []).map((item) => ({
            ...item,
            bomId: idOf(bom._id),
            fixture_document_number: bom.fixture_document_number,
          }))
        );

        setItems(allItems);
      } catch (error) {
        console.error('Failed to load manufacturing data:', error);
        ShowToast(
          'Failed to load manufacturing data',
          TYPE_FAIL,
          INTERVAL,
          props,
          ''
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [props]);

  /* helper: key for step */
  const keyForStep = (rowData, step) => {
    const itemId = idOf(rowData?._id);
    const stepId =
      idOf(step?.step_ref) ?? String(step?.position ?? '');
    return `${itemId}::${stepId}`;
  };

  /* update step field in state */
  const setStepField = useCallback((itemIndex, stepIndex, key, value) => {
    setItems((prev) => {
      const copy = [...prev];
      const item = { ...copy[itemIndex] };
      const steps = Array.isArray(item.manufacturing_steps)
        ? [...item.manufacturing_steps]
        : [];
      steps[stepIndex] = { ...steps[stepIndex], [key]: value };
      item.manufacturing_steps = steps;
      copy[itemIndex] = item;
      copy[itemIndex].hasChanges = true;
      return copy;
    });
  }, []);

  /* handle status change */
  const handleStepStatusChange = async (
    parentItemIndex,
    stepIndex,
    newStatus
  ) => {
    const parentItem = items[parentItemIndex];
    const stepToUpdate = parentItem.manufacturing_steps[stepIndex];

    const originalStatus = stepToUpdate.status;
    const originalCompletionDate = stepToUpdate.actual_completion_date;
    const originalCurrentStatus = stepToUpdate.current_status;

    let completionDate = null;
    let newCurrentStatus = '-';

    if (newStatus === 'Completed') {
      completionDate = new Date().toISOString();
      const actual = new Date(completionDate);
      const defined = stepToUpdate.defined_completion_date ? new Date(stepToUpdate.defined_completion_date) : null;

      if (defined) {
        const actualNorm = new Date(actual);
        const definedNorm = new Date(defined);
        actualNorm.setHours(0, 0, 0, 0);
        definedNorm.setHours(0, 0, 0, 0);

        const diffTime = actualNorm.getTime() - definedNorm.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays <= 1) {
          newCurrentStatus = 'On Time';
        } else if (diffDays <= 3) {
          newCurrentStatus = 'Warning';
        } else {
          newCurrentStatus = 'Delayed';
        }
      } else {
        newCurrentStatus = 'On Time';
      }
    }

    setStepField(parentItemIndex, stepIndex, 'status', newStatus);
    setStepField(
      parentItemIndex,
      stepIndex,
      'actual_completion_date',
      completionDate
    );
    setStepField(parentItemIndex, stepIndex, 'current_status', newCurrentStatus);

    const payload = {
      status: newStatus,
      updated_by: idOf(initialUser._id),
      current_status: newCurrentStatus,
    };
    if (newStatus === 'Completed') {
      payload.actual_completion_date = completionDate;
    } else {
      payload.actual_completion_date = null;
    }

    try {
      await updatestepfrombom(
        {},
        parentItem.bomId,
        idOf(parentItem._id),
        idOf(stepToUpdate.step_ref),
        payload
      );
      ShowToast('Step status updated!', TYPE_SUCCESS, INTERVAL);
    } catch (err) {
      // Revert UI changes on API failure
      setStepField(
        parentItemIndex,
        stepIndex,
        'status',
        originalStatus
      );
      setStepField(
        parentItemIndex,
        stepIndex,
        'actual_completion_date',
        originalCompletionDate ?? null
      );
      setStepField(
        parentItemIndex,
        stepIndex,
        'current_status',
        originalCurrentStatus ?? '-'
      );
      ShowToast('Failed to update step status', TYPE_FAIL, INTERVAL);
    }
  };

  /* download attachment */
  const handleDownloadForStep = async (url) => {
    try {
      const m = String(url || '').match(
        /^https:\/\/[^/]+\.s3\.amazonaws\.com\/(.+)$/
      );
      const fileKey = m?.[1] || url;
      const resp = await downloadDocumentBucket({}, { fileKey });
      window.open(resp?.data?.downloadUrl || url, '_blank');
    } catch (e) {
      console.error(e);
      ShowToast('Could not open file', TYPE_FAIL, INTERVAL, null, '');
    }
  };

  /* clear attachment */
  const clearAttachmentForStep = (itemIndex, stepIndex) => {
    setItems((prev) => {
      const copy = [...prev];
      const item = { ...copy[itemIndex] };
      const steps = [...item.manufacturing_steps];
      steps[stepIndex] = { ...steps[stepIndex], attachments: [] };
      item.manufacturing_steps = steps;
      copy[itemIndex] = item;
      copy[itemIndex].hasChanges = true;
      return copy;
    });
  };

  /* upload attachment */
  const uploadAttachmentForStep = async (rowData, stepObj, file) => {
    if (!file) return;

    const stepKey = keyForStep(rowData, stepObj);
    const itemIndex = items.findIndex(it => idOf(it._id) === idOf(rowData._id));
    const stepIndex = (rowData.manufacturing_steps || []).findIndex(s => keyForStep(rowData, s) === stepKey);

    if (itemIndex === -1 || stepIndex === -1) return;

    setItems(prev => {
        const newItems = [...prev];
        const newItem = { ...newItems[itemIndex] };
        const newSteps = [...newItem.manufacturing_steps];
        newSteps[stepIndex] = { ...newSteps[stepIndex], is_uploading: true, upload_progress: 0 };
        newItem.manufacturing_steps = newSteps;
        newItems[itemIndex] = newItem;
        newItem.hasChanges = true;
        return newItems;
    });

    try {
        const pres = await getS3PresignedUrl({}, { fileName: file.name, fileType: file.type || 'application/octet-stream' });
        const { url: putUrl, fileUrl: publicUrl } = pres.data;
        if (!putUrl || !publicUrl) throw new Error('Failed to get upload URL');

        await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', putUrl, true);
            xhr.setRequestHeader(HTTP_HEADER_KEYS.CONTENT_TYPE, file.type || HTTP_CONTENT_TYPES.OCTET_STREAM);

            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    const pct = Math.round((e.loaded / e.total) * 100);
                    setItems(currentItems => {
                        const newItems = [...currentItems];
                        const newItem = { ...newItems[itemIndex] };
                        const newSteps = [...newItem.manufacturing_steps];
                        newSteps[stepIndex] = { ...newSteps[stepIndex], upload_progress: pct };
                        newItem.manufacturing_steps = newSteps;
                        newItems[itemIndex] = newItem;
                        return newItems;
                    });
                }
            };
            xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
            xhr.onerror = () => reject(new Error('Network error during upload'));
            xhr.send(file);
        });

        let file_key = '';
        try { new URL(putUrl).pathname.replace(/^\//, '').split('/').slice(1).join('/') } catch {}

        setItems(prev => {
            const newItems = [...prev];
            const newItem = { ...newItems[itemIndex] };
            const newSteps = [...newItem.manufacturing_steps];
            newSteps[stepIndex] = {
                ...newSteps[stepIndex],
                attachments: [{ file_name: file.name, file_type: file.type || '', file_url: publicUrl, file_key }]
            };
            newItem.manufacturing_steps = newSteps;
            newItems[itemIndex] = newItem;
            return newItems;
        });

    } catch (err) {
        console.error('Step attachment upload failed:', err);
        ShowToast('Upload failed', TYPE_FAIL, INTERVAL, null, "");
    } finally {
        setItems(prev => {
            const newItems = [...prev];
            const newItem = { ...newItems[itemIndex] };
            const newSteps = [...newItem.manufacturing_steps];
            if (newSteps[stepIndex]) {
                const { is_uploading, upload_progress, ...restOfStep } = newSteps[stepIndex];
                newSteps[stepIndex] = restOfStep;
                newItem.manufacturing_steps = newSteps;
                newItems[itemIndex] = newItem;
            }
            return newItems;
        });
    }
};

  /* save updates */
  const handleSavePart = async (rowData) => {
    try {
      const payload = {
            _id: rowData._id,
            part_ref: rowData.part_ref,
            process_type_id: rowData.process_type_id,
            document_number: rowData.document_number,
            quantity: Number(rowData.quantity),
            procurement_done: Boolean(rowData.procurement_done),
            attachments: Array.isArray(rowData.attachments) ? rowData.attachments : [],
            manufacturing_steps: rowData.manufacturing_steps || [],
      };
      // reset haschange flag
      setItems((prev) =>
        prev.map((item) =>
          item._id === rowData._id ? { ...item, hasChanges: false } : item
        )
      );
      await updateBomItemPartial({}, rowData.bomId, rowData._id, payload );
      ShowToast('Part updated successfully!', TYPE_SUCCESS, INTERVAL, props, '');
    } catch (err) {
      console.error('Update failed', err);
      ShowToast('Failed to save part', TYPE_FAIL, INTERVAL, props, '');
    }
  };
  

  /* detail panel */
  const detailPanel = useCallback(
    (rowData) => {
      const idx = rowData?.tableData?.id;
      const steps = rowData?.manufacturing_steps || [];
        // Assuming user has all permissions on this page for UI purposes
      const canViewInternalDetails = true;
      const canMarkComplete = true;
      const canManageAttachments = true;
      const canEditBufferDays = true;
      const canEditActualCompletionDate = true;

      return (
            <div className="detail-panel-container">
                {!steps.length ? (
                    <h6>Steps are not visible at this stage</h6>
                ) : (
                    <TableContainer component={Paper} elevation={0} className="detail-table-container">
                        <Table size="small" aria-label="manufacturing steps">
                            <TableHead className="detail-table-header">
                                <TableRow>
                                    <TableCell className="detail-table-cell detail-table-header-cell">Step</TableCell>
                                    <TableCell className="detail-table-cell detail-table-header-cell">Completed</TableCell>
                                      <TableCell className="detail-table-cell detail-table-header-cell">Predef. Days</TableCell>
                                    <TableCell className="detail-table-cell detail-table-header-cell">Defined Completion</TableCell>
                                    <TableCell className="detail-table-cell detail-table-header-cell">Current Status</TableCell>
                                    <TableCell className="detail-table-cell detail-table-header-cell">Attachment</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {steps.map((s, si) => {
                                    const stepKey = keyForStep(rowData, s);
                                    const attachment = s.attachments?.[0];
                                    const isUploading = !!s.is_uploading; 
                                    const pct = s.upload_progress ?? 0;
                                    const statusChip = {
                                        'On Time': { label: 'On Time', color: 'success', variant: 'outlined' },
                                        'Warning': { label: 'Warning', color: 'warning', variant: 'outlined' },
                                        'Delayed': { label: 'Delayed', color: 'error', variant: 'filled' },
                                    }[s.current_status] || { label: '-', color: 'default' };

                                    return (
                                        <TableRow key={s.step_ref || si} hover>
                                            <TableCell className="detail-table-cell">
                                                <div className="step-title">{s.step_title_snapshot || '—'}</div>
                                                <div className="step-position">
                                                    Step: {typeof s.position === 'number' ? s.position + 1 : si + 1}
                                                </div>
                                            </TableCell>
                                            
                                            <TableCell className="icon-cell cell-align-center">
                                                {(() => {
                                                    const isSuperAdmin = initialUser?.user_role === SUPER_ADMIN;
                                                    const isAnyPreviousStepPending = si > 0 && steps.slice(0, si).some(prevStep => prevStep.status !== 'Completed');
                                                    const isAnySubsequentStepCompleted = steps.slice(si + 1).some(nextStep => nextStep.status === 'Completed');

                                                    let finalDisabled = false;
                                                    let tooltipTitle = 'Mark as Completed';

                                                    if (s.status === 'Completed') {
                                                        if (isSuperAdmin) {
                                                            if (isAnySubsequentStepCompleted) {
                                                                    finalDisabled = true;
                                                                    tooltipTitle = 'Uncheck subsequent steps first';
                                                            } else {
                                                                    finalDisabled = false;
                                                                    tooltipTitle = 'Uncheck / Mark as Pending';
                                                            }
                                                        } else {
                                                            finalDisabled = true;
                                                            tooltipTitle = 'This step is already completed';
                                                        }
                                                    } else {
                                                        finalDisabled = isAnyPreviousStepPending || !canMarkComplete;
                                                        if (isAnyPreviousStepPending) {
                                                            tooltipTitle = 'Complete previous steps first';
                                                        }
                                                    }
                                                    
                                                    return (
                                                        <Tooltip title={tooltipTitle}>
                                                            <span>
                                                                <IconButton
                                                                    disabled={finalDisabled}
                                                                    onClick={() => {
                                                                        const nextStatus = s.status === 'Completed' ? 'Pending' : 'Completed';
                                                                        handleStepStatusChange(idx, si, nextStatus);
                                                                    }}
                                                                    color={s.status === 'Completed' ? 'success' : 'default'}
                                                                    size="small"
                                                                >
                                                                  { s.status === 'Completed' ? (
                                                                      <CheckCircleSharpIcon
                                                                          className="icon-size"
                                                                          sx={{ color: 'green' }}
                                                                      />
                                                                  ) : (
                                                                      <CheckCircleSharpIcon
                                                                          className="icon-size"
                                                                          sx={{ color: 'grey', opacity: finalDisabled ? 0.2 : 0.5 }}
                                                                      />
                                                                  )}

                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    );
                                                })()}
                                            </TableCell>

                                            <TableCell className="detail-table-cell cell-align-center">
                                                <TextField type="number" size="small" variant="standard" value={s.predefined_duration_days ?? 0} InputProps={{ readOnly: true, inputProps: { min: 0, style: { textAlign: 'center' } } }} sx={{ maxWidth: 80 }}/>
                                            </TableCell>
                                            
                                            <TableCell className="detail-table-cell">
                                                 <TextField type="date" size="small" variant="standard" value={s.defined_completion_date ? moment(s.defined_completion_date).format('YYYY-MM-DD') : ''} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} fullWidth />
                                            </TableCell>
                                           <TableCell className="detail-table-cell cell-align-center">
                                                    <Chip size="small" label={statusChip.label} color={statusChip.color} variant={statusChip.variant} />
                                            </TableCell>



                                            <TableCell className="detail-table-cell">
                                                {isUploading ? (
                                                    <Box sx={{ width: 150, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LinearProgress variant="determinate" value={pct} sx={{ width: '100%', height: 6, borderRadius: 2 }} />
                                                        <Typography variant="caption" color="text.secondary">{`${pct}%`}</Typography>
                                                    </Box>
                                                ) : attachment ? (
                                                    <Chip
                                                        size="small"
                                                        icon={<CloudUploadIcon />}
                                                        label={attachment.file_name || 'file'}
                                                        onClick={() => handleDownloadForStep(attachment.file_url)}
                                                        onDelete={() => clearAttachmentForStep(idx, si)}
                                                        deleteIcon={<DeleteForeverIcon />}
                                                        variant="outlined"
                                                        sx={{ maxWidth: '150px', '& .MuiChip-label': { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' } }}
                                                    />
                                                ) : (
                                                    canManageAttachments && (
                                                        <Tooltip title="Attach file">
                                                            <label htmlFor={`step-file-${stepKey}`} style={{ cursor: 'pointer' }}>
                                                                <input
                                                                    id={`step-file-${stepKey}`} type="file" hidden
                                                                    onChange={(e) => {
                                                                        const f = e.target.files?.[0];
                                                                        if (f) uploadAttachmentForStep(rowData, s, f);
                                                                        e.target.value = '';
                                                                    }}
                                                                />
                                                                <IconButton size="small" component="span"><CloudUploadIcon /></IconButton>
                                                            </label>
                                                        </Tooltip>
                                                    )
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>
        );
    },
    [items] 
);

  const tableOptions = {
    actionsColumnIndex: -1,
    headerStyle: {
      color: '#2d3d42',
      fontWeight: 'bolder',
      fontSize: 14,
    },
    sorting: true,
    columnsButton: true,
    exportButton: true,
    exportFileName: 'Manufacturing Project List',
    pageSize: 5,
    pageSizeOptions: [5, 10, 15, 20],
    cellStyle: {
      color: '#2d3d42',
      fontSize: 12
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="main-content manufacturing-page">
      <div className="pagetitle row" style={{ color: 'black', marginTop: "-25px" }}>
        <div className="col-md-10 sm_device">
          <FlashOnIcon aria-hidden="true" />
          <span style={{ marginLeft: '7px' }}>Manufacturing Page</span>
        </div>
        <div className="col-md-2 sm_device" style={{ textAlign: 'right' }}>
          <i className={`${CALENDER_ICON}`}></i> {moment().format('DD MMM YYYY')}
        </div>
      </div>
      <div style={{ borderBottom: '1px solid lightgray', marginBottom: 15, marginTop: 15 }} />
      <div id="manufacturingTable" className="mt-4 border list-table-div project-list-table-div" style={{ marginBottom: 50 }}> {/* <-- WRAPPER DIV WITH ID */}
        <ThemeProvider theme={theme}>
          <MaterialTable
            title={null}
            columns={[
              { title: 'Document Number', field: 'document_number' },
              {
                title: 'Part Name',
                field: 'part_ref',
                render: (row) => partMap[row.part_ref] || '—',
              },
              { title: 'Fixture Doc Number', field: 'fixture_document_number' },
              {
                title: ' ',
                field: 'actions',
                sorting: false,
                render: (rowData) =>
                  rowData.hasChanges ? (
                    <Tooltip title="Save Part">
                      <IconButton
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation(); 
                          handleSavePart(rowData);
                        }}
                      >
                        <SaveIcon  fontSize="large"/>
                      </IconButton>
                    </Tooltip>
                  ) : null,
              },
            ]}
            data={items}
            components={{ Container: (p) => <Paper {...p} elevation={0} variant="outlined" /> }}
            detailPanel={detailPanel}
            onRowClick={(evt, rowData, togglePanel) => togglePanel && togglePanel()}
            options={{ ...tableOptions, actionsColumnIndex: -1 }}
          />
        </ThemeProvider>
      </div>
    </div>
  );
};

export default ManufacturingUpdate;